import { useEffect } from 'react';
import { create } from 'zustand';

import { api, AUTH_LOGOUT_EVENT, errorCodeOf, logoutRequest, refreshTokens, tokenStore } from '@/shared/api/client';
import { DELETED_ACCOUNT_STORAGE_KEY } from '@/shared/api/client';
import type { Me, TelegramWidgetPayload, TokenPair } from '@/shared/api/types';
import { applyRemoteLocale } from '@/shared/i18n';

import { detectIsTMA, getWebApp } from './useTelegram';

/**
 * Аутентификация (D-09): два пути входа — TMA initData и Telegram Login Widget,
 * оба сводятся к одной паре access/refresh.
 *
 * `role` приходит только с сервера и никогда не отправляется с фронта:
 * повышение прав через клиент невозможно по определению (D-08).
 */

export type AuthStatus = 'loading' | 'anon' | 'ready';

interface AuthStore {
  me: Me | null;
  status: AuthStatus;
  /** Код ошибки для `errors.<code>` из словаря. */
  error: string | null;
  /** Идёт операция входа/выхода. */
  pending: boolean;
  /** Флаг что bootstrap уже запущен — защита от повторных вызовов */
  _bootstrapped: boolean;
  /** Timestamp последнего запуска bootstrap для защиты от циклов */
  _lastBootstrap: number;
  setMe(me: Me | null): void;
  clearError(): void;
  bootstrap(): Promise<void>;
  loginTMA(): Promise<boolean>;
  loginWidget(payload: TelegramWidgetPayload): Promise<boolean>;
  logout(): Promise<void>;
}

async function applyPair(pair: TokenPair): Promise<Me> {
  tokenStore.setPair(pair);
  await applyRemoteLocale(pair.user.locale);
  return pair.user;
}

export const useAuthStore = create<AuthStore>()((set, get) => ({
  me: null,
  status: 'loading',
  error: null,
  pending: false,
  _bootstrapped: false,
  _lastBootstrap: 0,

  setMe: (me) => set({ me, status: me ? 'ready' : 'anon' }),
  clearError: () => set({ error: null }),

  /**
   * Порядок попыток: внутри Telegram — сразу initData (пользователь не должен
   * видеть экран входа вообще); в вебе — восстановление по refresh-токену;
   * иначе аноним и экран /auth.
   *
   * БЕЗОПАСНОСТЬ (критично): при отсутствии валидной сессии статус ДОЛЖЕН быть
   * 'anon', чтобы guards редиректили на /auth. Никакой контент не рендерится
   * без подтверждённой аутентификации.
   */
  bootstrap: async () => {
    const now = Date.now();
    const state = get();

    // Защита от спама: проверяем localStorage для защиты между перезагрузками
    const lastBootstrapStored = parseInt(localStorage.getItem('_lastBootstrap') || '0', 10);
    const timeSinceLastBootstrap = now - Math.max(state._lastBootstrap, lastBootstrapStored);

    if (state._bootstrapped && timeSinceLastBootstrap < 10000) {
      return;
    }

    set({ _bootstrapped: true, _lastBootstrap: now });
    localStorage.setItem('_lastBootstrap', String(now));

    // После удаления аккаунта ждём явного повторного входа, чтобы TMA initData
    // не создал новый аккаунт сразу после перенаправления.
    if (detectIsTMA() && localStorage.getItem(DELETED_ACCOUNT_STORAGE_KEY) === '1') {
      set({ me: null, status: 'anon' });
      return;
    }

    // В Telegram Mini App: автологин через initData
    if (detectIsTMA()) {
      const ok = await get().loginTMA();
      if (ok) return;
      // Неудача в TMA: невалидный initData или проблема сервера
      tokenStore.clear();
      set({ me: null, status: 'anon' });
      return;
    }

    // Веб-версия: восстановление сессии по refresh-токену
    if (tokenStore.hasRefresh()) {
      set({ status: 'loading' });
      const refreshed = await refreshTokens();
      if (refreshed) {
        try {
          const me = await api.get<Me>('/client/me');
          await applyRemoteLocale(me.locale);
          set({ me, status: 'ready', error: null });
          return;
        } catch {
          // Токен невалиден или пользователь удалён
          tokenStore.clear();
        }
      } else {
        // Refresh провалился: токен протух или отозван
        tokenStore.clear();
      }
    }

    // Нет валидной сессии: статус 'anon' → редирект на /auth через guards
    set({ me: null, status: 'anon' });
  },

  loginTMA: async () => {
    const webApp = getWebApp();
    const initData = webApp?.initData ?? '';
    if (initData === '') {
      set({ status: 'anon', error: 'invalid_init_data' });
      return false;
    }
    set({ pending: true, error: null });
    try {
      const pair = await api.post<TokenPair>(
        '/auth/tma',
        {
          init_data: initData,
          // start_param несёт реферальный код; сервер применит его только при
          // первом создании пользователя (docs/API.md §1).
          ref: webApp?.initDataUnsafe.start_param,
        },
        { anonymous: true },
      );
      const me = await applyPair(pair);
      set({ me, status: 'ready', pending: false, error: null });
      return true;
    } catch (error) {
      set({ pending: false, status: 'anon', error: errorCodeOf(error) });
      return false;
    }
  },

  loginWidget: async (payload) => {
    window.localStorage.removeItem(DELETED_ACCOUNT_STORAGE_KEY);
    set({ pending: true, error: null });
    try {
      const pair = await api.post<TokenPair>('/auth/widget', payload, { anonymous: true });
      const me = await applyPair(pair);
      set({ me, status: 'ready', pending: false, error: null });
      return true;
    } catch (error) {
      set({ pending: false, status: 'anon', error: errorCodeOf(error) });
      return false;
    }
  },

  logout: async () => {
    set({ pending: true });
    await logoutRequest();
    set({ me: null, status: 'anon', pending: false, error: null });
  },
}));

/**
 * Клиент кричит `auth:logout`, когда refresh окончательно не удался.
 * Подписка на уровне модуля, а не хука: событие может прийти в момент, когда
 * ни один компонент с useAuth не смонтирован (фоновая инвалидация).
 */
if (typeof window !== 'undefined') {
  window.addEventListener(AUTH_LOGOUT_EVENT, () => {
    useAuthStore.setState({ me: null, status: 'anon', pending: false });
  });
}

/** Однократный старт сессии. Вызывается один раз из провайдеров. */
export function useAuthBootstrap(): void {
  useEffect(() => {
    void useAuthStore.getState().bootstrap();
  }, []);
}

export interface AuthApi {
  me: Me | null;
  status: AuthStatus;
  error: string | null;
  pending: boolean;
  isStaff: boolean;
  isOwner: boolean;
  setMe(me: Me | null): void;
  clearError(): void;
  loginTMA(): Promise<boolean>;
  loginWidget(payload: TelegramWidgetPayload): Promise<boolean>;
  logout(): Promise<void>;
}

export function useAuth(): AuthApi {
  const me = useAuthStore((state) => state.me);
  const status = useAuthStore((state) => state.status);
  const error = useAuthStore((state) => state.error);
  const pending = useAuthStore((state) => state.pending);
  const setMe = useAuthStore((state) => state.setMe);
  const clearError = useAuthStore((state) => state.clearError);
  const loginTMA = useAuthStore((state) => state.loginTMA);
  const loginWidget = useAuthStore((state) => state.loginWidget);
  const logout = useAuthStore((state) => state.logout);

  return {
    me,
    status,
    error,
    pending,
    isStaff: me?.role === 'admin' || me?.role === 'owner',
    isOwner: me?.role === 'owner',
    setMe,
    clearError,
    loginTMA,
    loginWidget,
    logout,
  };
}
